/**
 * `/actions` — three views, and the reminder setting.
 *
 * **`Waiting on customer` is first-class and separate.** Items owned by a
 * customer contact are where the funnel is actually stuck, and conflating them
 * with internal work is the thing most systems get wrong: a rep looking at
 * "twelve open actions" cannot tell which ones are theirs to do.
 *
 * The reminder setting shows **a rendered sample of the digest beside it**
 * (§8e). The email deliberately contains only opp numbers, counts and links —
 * no account name, no action title, no money — and a user who does not know
 * that reads "OPP-01042 — 1 overdue" as a broken email rather than a
 * deliberate one.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { EMAIL_REMINDER_MODES } from '@menlo/shared';
import { ME_QUERY_KEY, useMe } from '@/auth/useMe';
import { useApiMutation, useApiQuery, type Paged, type Row } from '@/lib/queries';
import { useUserLookup } from '@/lib/reference';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { TabList, TabPanel, useTab, type TabDefinition } from '@/components/ui/tabs';
import {
  ACTIONS_QUERY_KEY,
  ActionItemRow,
  BUCKET_LABELS,
  bucketOf,
  isOpen,
  useQuickAdd,
  type Bucket,
} from '@/features/actions/ActionItems';
import { daysSince, humanize } from '@/lib/format';

const BUCKET_ORDER: Bucket[] = ['overdue', 'today', 'week', 'later', 'nodate'];

export function ActionsPage(): React.ReactElement {
  const { data: me } = useMe();
  const users = useUserLookup();
  const quickAdd = useQuickAdd({ label: 'Not linked to an opportunity.' });

  /**
   * One page of open work, ordered by due date. Cap stated rather than hidden:
   * a truncated list that says nothing is a wrong answer presented as a right
   * one, which is the same defect §9 names for counts.
   */
  const query = useApiQuery<Paged<Row>>(
    [...ACTIONS_QUERY_KEY, 'page'],
    '/api/action-items?pageSize=200&sort=due_date',
  );
  const all = query.data?.items ?? [];
  const open = all.filter(isOpen);

  const mine = open.filter(
    (item) => item['owner_type'] !== 'customer' && item['owner_user_id'] === me?.user.id,
  );
  const waiting = open.filter((item) => item['owner_type'] === 'customer');
  const isManager = me?.tier === 'Manager' || me?.tier === 'Admin';

  const tabs: TabDefinition[] = [
    { id: 'mine', label: 'My actions', count: mine.length },
    { id: 'waiting', label: 'Waiting on customer', count: waiting.length },
    ...(isManager ? [{ id: 'team', label: 'Team actions', count: open.length }] : []),
    { id: 'reminders', label: 'Reminders' },
  ];
  const [tab, setTab] = useTab(tabs);

  const blockers = useMemo(() => new Map(all.map((item) => [String(item['id']), item])), [all]);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold">Actions</h1>
          <p className="text-xs text-muted-foreground">
            {query.data !== undefined && query.data.total > all.length
              ? `Showing the ${all.length} soonest of ${query.data.total}.`
              : `${open.length} open.`}{' '}
            Press <kbd className="rounded border px-1">a</kbd> anywhere to add one.
          </p>
        </div>
        {quickAdd.button}
      </header>

      <TabList tabs={tabs} active={tab} onSelect={setTab} />

      <TabPanel id="mine" active={tab}>
        <BucketedList items={mine} blockers={blockers} emptyText="Nothing open assigned to you." />
      </TabPanel>

      <TabPanel id="waiting" active={tab}>
        <p className="mb-2 text-xs text-muted-foreground">
          Owned by someone at the customer. These are the ones a chase email is for.
        </p>
        <div className="rounded-md border">
          {waiting.length === 0 && (
            <p className="px-3 py-6 text-center text-xs text-muted-foreground">
              Nothing is waiting on a customer.
            </p>
          )}
          {waiting.map((item) => (
            <div key={String(item['id'])} className="flex items-baseline gap-3">
              <span className="w-24 shrink-0 pl-3 pt-2 text-[11px] text-muted-foreground">
                {waitingFor(item)}
              </span>
              <div className="min-w-0 flex-1">
                <ActionItemRow item={item} canWrite blockers={blockers} />
              </div>
            </div>
          ))}
        </div>
      </TabPanel>

      {isManager && (
        <TabPanel id="team" active={tab}>
          <TeamActions items={open} users={users} blockers={blockers} />
        </TabPanel>
      )}

      <TabPanel id="reminders" active={tab}>
        <ReminderSettings />
      </TabPanel>

      {quickAdd.dialog}
    </div>
  );
}

function waitingFor(item: Row): string {
  const days = daysSince(item['due_date'] as string | null);
  if (days === null) return 'no date';
  if (days <= 0) return 'not yet due';
  return `${days}d waiting`;
}

function BucketedList({
  items,
  blockers,
  emptyText,
}: {
  items: Row[];
  blockers: Map<string, Row>;
  emptyText: string;
}): React.ReactElement {
  const grouped = new Map<Bucket, Row[]>();
  for (const item of items) {
    const bucket = bucketOf(item);
    grouped.set(bucket, [...(grouped.get(bucket) ?? []), item]);
  }

  if (items.length === 0) {
    return (
      <p className="rounded-md border border-dashed px-3 py-6 text-center text-xs text-muted-foreground">
        {emptyText}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {BUCKET_ORDER.filter((bucket) => (grouped.get(bucket) ?? []).length > 0).map((bucket) => (
        <section key={bucket}>
          <h2 className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {BUCKET_LABELS[bucket]}
            <Badge variant={bucket === 'overdue' ? 'danger' : 'outline'}>
              {(grouped.get(bucket) ?? []).length}
            </Badge>
          </h2>
          <div className="rounded-md border">
            {(grouped.get(bucket) ?? []).map((item) => (
              <ActionItemRow key={String(item['id'])} item={item} canWrite blockers={blockers} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/**
 * Team actions, with an overdue-by-owner rollup.
 *
 * It shows every open item rather than only a manager's direct reports:
 * `manager_user_id` is the only hierarchy in v1 and §7 records it as
 * *informational*, so filtering by it would present a partial list as a
 * complete one. Everyone reads everything (§2), so nothing is being widened
 * here — only the grouping.
 */
function TeamActions({
  items,
  users,
  blockers,
}: {
  items: Row[];
  users: Map<string, { display_name: string }>;
  blockers: Map<string, Row>;
}): React.ReactElement {
  const byOwner = new Map<string, Row[]>();
  for (const item of items) {
    const owner = String(item['owner_user_id'] ?? 'unassigned');
    byOwner.set(owner, [...(byOwner.get(owner) ?? []), item]);
  }

  return (
    <div className="space-y-4">
      <table className="w-full text-sm">
        <caption className="sr-only">Overdue actions by owner</caption>
        <thead>
          <tr className="text-left text-xs text-muted-foreground">
            <th scope="col" className="py-1">
              Owner
            </th>
            <th scope="col" className="py-1 text-right">
              Open
            </th>
            <th scope="col" className="py-1 text-right">
              Overdue
            </th>
          </tr>
        </thead>
        <tbody>
          {[...byOwner.entries()].map(([owner, owned]) => (
            <tr key={owner} className="border-t">
              <td className="py-1">{users.get(owner)?.display_name ?? 'Unassigned / customer'}</td>
              <td className="py-1 text-right tabular-nums">{owned.length}</td>
              <td className="py-1 text-right tabular-nums">
                {owned.filter((item) => bucketOf(item) === 'overdue').length}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="rounded-md border">
        {items.map((item) => (
          <ActionItemRow key={String(item['id'])} item={item} canWrite blockers={blockers} />
        ))}
      </div>
    </div>
  );
}

/**
 * The per-user reminder setting, with the sample.
 *
 * The weekly caveat is stated beside the setting rather than left to be
 * discovered: `weekly` delivers the same overdue / today / tomorrow window as
 * `daily`, so a weekly subscriber sees "due tomorrow" only on Mondays and
 * everything due Tuesday to Sunday reaches them already overdue (§8e).
 */
function ReminderSettings(): React.ReactElement {
  const { data: me } = useMe();
  const [mode, setMode] = useState<string>(me?.user.email_reminders ?? 'daily');

  const save = useApiMutation<{ updated: boolean }, string>(
    (value) => ({
      path: '/api/me/preferences',
      method: 'PUT',
      body: { email_reminders: value },
    }),
    { invalidate: [ME_QUERY_KEY] },
  );

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <div className="space-y-3">
        <h2 className="text-sm font-medium">Email reminders</h2>
        <Select
          aria-label="Email reminders"
          className="max-w-xs"
          value={mode}
          onChange={(event) => {
            setMode(event.target.value);
            save.mutate(event.target.value);
          }}
        >
          {EMAIL_REMINDER_MODES.map((value) => (
            <option key={value} value={value}>
              {humanize(value)}
            </option>
          ))}
        </Select>

        <div className="space-y-2 text-xs text-muted-foreground">
          <p>
            <strong>Daily</strong> sends one message per day, after 7am in your own timezone (
            {me?.user.timezone ?? '—'}), covering everything overdue, due today, and due tomorrow.
            One message, never one per item.
          </p>
          <p>
            <strong>Weekly</strong> sends on Mondays — but it carries the same overdue / today /
            tomorrow window, so you will see &ldquo;due tomorrow&rdquo; only on a Monday and
            anything due Tuesday to Sunday reaches you already overdue. That is a real limitation,
            not a bug.
          </p>
          <p>
            <strong>Off</strong> stops them entirely. Nothing else changes; the items are still
            here.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <h2 className="text-sm font-medium">What it looks like</h2>
        <pre className="overflow-x-auto rounded-md border bg-muted/40 p-3 text-[11px] leading-relaxed">
          {`Subject: Menlo Funnel — 4 items need attention

You have 4 items needing attention.

  OPP-01042    1 overdue, 1 due today      →
  OPP-01187    1 due tomorrow              →
  OPP-01204    1 overdue                   →
  Not linked   1 overdue                   →

Open the funnel →`}
        </pre>
        <p className="text-xs text-muted-foreground">
          Deliberately identifiers and links only — no customer name, no action title, no money, no
          stage. Opportunity names and action titles are free text, so no policy that includes them
          is enforceable, and this mailbox is reachable from a personal phone in any country. The
          link puts the detail back behind sign-in, on the deal&rsquo;s Actions tab.
        </p>
        <p className="text-xs text-muted-foreground">
          <Link to="/actions" className="underline underline-offset-2">
            Items with no opportunity
          </Link>{' '}
          get their own line rather than being dropped.
        </p>
      </div>
    </div>
  );
}
